import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { COLORS, UI } from '../config/colors';
import { fetchRoleTypes } from '../utils/api';

// Fallback role types data in case API fails
const fallbackRoleTypesData = [
  {
    id: "TECHNICAL",
    name: "Technical / Skill-Based",
    description: "Roles that require specific technical skills or specialized knowledge.",
    examples: ["Software Engineer", "Data Analyst", "Electrician", "DevOps Engineer", "System Administrator"],
    discoveryOrder: ["skills", "location", "title", "company", "experience", "education"]
  },
  {
    id: "CREATIVE",
    name: "Creative / Strategic",
    description: "Roles focused on creative output, design, or strategic planning.",
    examples: ["Designer", "Content Strategist", "Product Marketing", "UX Designer", "Brand Manager"],
    discoveryOrder: ["title", "location", "company", "skills", "experience", "education"]
  },
  {
    id: "LEADERSHIP",
    name: "People Leadership / Management",
    description: "Roles involving team leadership, management, or executive functions.",
    examples: ["Team Lead", "Director", "VP", "Manager", "Chief Executive Officer"],
    discoveryOrder: ["title", "company", "location", "experience", "education", "skills"]
  },
  {
    id: "INTERPERSONAL",
    name: "Interpersonal / Soft-Skill Focused",
    description: "Roles centered on communication, relationship-building, and interpersonal skills.",
    examples: ["Sales", "Customer Support", "Recruiter", "Account Manager", "Business Development"],
    discoveryOrder: ["title", "location", "company", "experience", "skills", "education"]
  },
  {
    id: "OPERATIONAL",
    name: "High-Volume / Operational",
    description: "Roles focused on day-to-day operations, often in high-volume environments.",
    examples: ["Forklift Operator", "Call Center Agent", "Warehouse Worker", "Retail Associate", "Delivery Driver"],
    discoveryOrder: ["title", "location", "company", "experience", "education", "skills"]
  }
];

const RoleTypeSelection = ({ onRoleTypeSelect, onClose }) => {
  const [selectedRoleType, setSelectedRoleType] = useState(null);
  const [roleTypes, setRoleTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch role types from the server when component mounts
  useEffect(() => {
    const getRoleTypes = async () => {
      setIsLoading(true);
      try {
        const data = await fetchRoleTypes();
        if (data && data.roleTypes && data.roleTypes.length > 0) {
          setRoleTypes(data.roleTypes);
        } else {
          // Fall back to the hardcoded data if the API call fails or returns empty data
          console.log('Using fallback role types data');
          setRoleTypes(fallbackRoleTypesData);
        }
      } catch (error) {
        console.error('Error fetching role types:', error);
        // Fall back to the hardcoded data if the API call fails
        setRoleTypes(fallbackRoleTypesData);
      } finally {
        setIsLoading(false);
      }
    };
    
    getRoleTypes();
  }, []);
  
  const handleRoleTypeSelect = (roleTypeId) => {
    setSelectedRoleType(roleTypeId);
  };
  
  const handleContinue = () => {
    if (selectedRoleType) {
      onRoleTypeSelect([selectedRoleType]); // Wrap in array for backward compatibility
    }
  };
  
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        width: '90%', 
        maxWidth: '700px',
        maxHeight: '80vh',
        zIndex: 900,
        overflow: 'auto',
        borderRadius: '12px',
        backgroundColor: '#FFFFFF'
      }}
    >
      {/* Modal Header */}
      <Box 
        sx={{ 
          padding: '24px 32px',
          borderBottom: '1px solid #EAECF0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#313131', 
              fontWeight: 500,
              fontSize: '18px',
              lineHeight: '1.33em'
            }}
          >
            What role type are you hiring for?
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#465366',
              fontSize: '14px',
              lineHeight: '1.43em'
            }}
          >
            Choose the category that best fits the role so we can tailor your intake experience.
          </Typography>
        </Box>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              color: '#465366',
              '&:hover': {
                backgroundColor: 'rgba(70, 83, 102, 0.04)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Modal Content */}
      <Box sx={{ padding: '0px 32px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress sx={{ color: '#167FF9' }} />
          </Box>
        ) : (
          <Box sx={{ py: 3 }}>
            {roleTypes.map((roleType) => (
              <Card 
                key={roleType.id}
                sx={{ 
                  mb: 2,
                  border: selectedRoleType === roleType.id ? '1px solid rgba(22, 127, 249, 0.6)' : '1px solid #EAECF0',
                  borderRadius: '8px',
                  backgroundColor: selectedRoleType === roleType.id ? 'rgba(225, 239, 255, 0.6)' : '#FFFFFF',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: selectedRoleType === roleType.id ? 'rgba(225, 239, 255, 0.6)' : '#F2F4F7'
                  }
                }}
                onClick={() => handleRoleTypeSelect(roleType.id)}
              >
                <CardContent sx={{ padding: '20px', display: 'flex', gap: '16px' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 500, 
                        color: '#101828',
                        fontSize: '14px',
                        lineHeight: '1.43em',
                        mb: '2px'
                      }}
                    >
                      {roleType.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#465366',
                        fontSize: '13px',
                        lineHeight: '1.38em'
                      }}
                    >
                      {roleType.description}
                    </Typography>
                    
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#465366',
                          fontSize: '13px',
                          lineHeight: '1.38em'
                        }}
                      >
                        Examples:
                      </Typography>
                      {roleType.examples.slice(0, 3).map((example, i) => (
                        <Chip 
                          key={i} 
                          label={example} 
                          size="small"
                          sx={{ 
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #EAECF0',
                            color: '#465366',
                            fontSize: '12px',
                            height: '24px',
                            '& .MuiChip-label': {
                              padding: '1px 6px'
                            }
                          }} 
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  {/* Radio/Check Indicator */}
                  {selectedRoleType === roleType.id ? (
                    <Box 
                      sx={{ 
                        width: 20, 
                        height: 20, 
                        borderRadius: '50%', 
                        backgroundColor: '#167FF9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Box 
                        component="span" 
                        sx={{ 
                          fontFamily: 'Material Icons Round',
                          color: '#FFFFFF',
                          fontSize: '16px',
                          lineHeight: 1
                        }}
                      >
                        check
                      </Box>
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        border: '1px solid #EAECF0',
                        opacity: 0
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Modal Footer */}
      <Box 
        sx={{ 
          padding: '0px 32px 24px',
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Button 
          variant="contained" 
          disabled={!selectedRoleType}
          onClick={handleContinue}
          sx={{ 
            backgroundColor: '#167FF9',
            color: '#FFFFFF',
            textTransform: 'none',
            padding: '8px 12px',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#1068CC'
            },
            '&.Mui-disabled': {
              backgroundColor: '#F2F4F7',
              color: '#969DAD'
            }
          }}
        >
          Continue
        </Button>
      </Box>
    </Paper>
  );
};

export default RoleTypeSelection;
